export type WelcomeTopic = 'que-es-rfw' | 'como-participar' | 'como-ganar-puntos' | 'como-subir-ranking' | 'como-entrar-clubes';

export type WelcomeFormat = 'paso' | 'video' | 'guia';

export interface WelcomeTopicConfig {
  key: WelcomeTopic;
  order: number;
  title: string;
  subtitle: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
  gradient: string;
  description: string;
}

export interface WelcomeStep {
  id: string;
  topic: WelcomeTopic;
  format: WelcomeFormat;
  order: number;
  title: string;
  description: string;
  icon: string;
  durationSeconds: number;
  visualUrl: string;
  ctaLabel: string;
  ctaLink: string;
}

export interface WelcomeVideo {
  id: string;
  topic: WelcomeTopic;
  title: string;
  durationSeconds: number;
  thumbnailUrl: string;
  description: string;
}

export interface WelcomeGuide {
  id: string;
  topic: WelcomeTopic;
  title: string;
  icon: string;
  color: string;
  bgColor: string;
  content: string;
  visualUrl: string;
}

export interface WelcomeStats {
  totalNewUsersToday: number;
  totalCompletedOnboarding: number;
  averageCompletionSeconds: number;
  completionRate: number;
  mostViewedTopic: string;
  mostSkippedTopic: string;
}

export interface WelcomeRule {
  id: number;
  title: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
}

export const welcomeTopicConfigs: WelcomeTopicConfig[] = [
  {
    key: 'que-es-rfw',
    order: 1,
    title: 'Qué es RFW',
    subtitle: 'Tu red social de radio',
    icon: 'ri-broadcast-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    gradient: 'from-emerald-400 to-teal-500',
    description: 'RFW es la red social donde viven las comunidades de radio. Escuchá, interactuá, competí y crecé con tu emisora favorita.',
  },
  {
    key: 'como-participar',
    order: 2,
    title: 'Cómo Participar',
    subtitle: 'Tu primera interacción',
    icon: 'ri-chat-smile-2-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    gradient: 'from-amber-400 to-orange-500',
    description: 'Entrá a una cabina, comentá en vivo, reaccioná a publicaciones y unite a la conversación. En RFW tu voz se escucha.',
  },
  {
    key: 'como-ganar-puntos',
    order: 3,
    title: 'Cómo Ganar Puntos',
    subtitle: 'Tu motor de progreso',
    icon: 'ri-coin-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    gradient: 'from-rose-400 to-pink-500',
    description: 'Cada acción suma: escuchar radio, comentar, compartir, participar en concursos y mantener tu racha diaria. Los puntos desbloquean niveles, insignias y beneficios.',
  },
  {
    key: 'como-subir-ranking',
    order: 4,
    title: 'Cómo Subir en el Ranking',
    subtitle: 'Tu posición en la comunidad',
    icon: 'ri-trophy-line',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    gradient: 'from-violet-400 to-purple-500',
    description: 'El Score RFW mide tu actividad. Sumá puntos siendo constante: escuchá a diario, participá en trivias, ganá competencias y unite a clubes. Cuanto más activo, más alto llegás.',
  },
  {
    key: 'como-entrar-clubes',
    order: 5,
    title: 'Cómo Entrar a Clubes',
    subtitle: 'Tu comunidad dentro de la comunidad',
    icon: 'ri-team-line',
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200',
    gradient: 'from-sky-400 to-cyan-500',
    description: 'Cada radio tiene su club de fans. Unite gratis, participá en retos, ganá insignias exclusivas y conocé oyentes que comparten tu pasión.',
  },
];

export const welcomeSteps: WelcomeStep[] = [
  // ---- TEMA 1: Qué es RFW ----
  {
    id: 'step-1-1',
    topic: 'que-es-rfw',
    format: 'paso',
    order: 1,
    title: 'Bienvenido a RFW',
    description: 'RadioFrecuenciaWeb es la plataforma que conecta radios, locutores, programas y oyentes en un solo lugar. Más de 2,500 emisoras, 180,000 oyentes activos y una comunidad que crece cada día.',
    icon: 'ri-radio-line',
    durationSeconds: 15,
    visualUrl: 'https://readdy.ai/api/search-image?query=Modern%20vibrant%20community%20radio%20platform%20with%20diverse%20group%20of%20people%20listening%20and%20interacting%20with%20radio%20shows%20glowing%20broadcast%20waves%20connecting%20people%20warm%20inviting%20atmosphere%20digital%20illustration%20style%20with%20soft%20gradients%20and%20clean%20composition%20editorial%20quality&width=800&height=450&seq=welcome-hero-rfw&orientation=landscape',
    ctaLabel: 'Empezar recorrido',
    ctaLink: '#',
  },
  {
    id: 'step-1-2',
    topic: 'que-es-rfw',
    format: 'guia',
    order: 2,
    title: 'El ecosistema RFW',
    description: 'Radios, programas, locutores, clubes, eventos, concursos, trivias, rankings. Todo conectado. Vos elegís dónde empezar.',
    icon: 'ri-global-line',
    durationSeconds: 12,
    visualUrl: 'https://readdy.ai/api/search-image?query=Infographic%20style%20digital%20illustration%20showing%20interconnected%20radio%20ecosystem%20with%20stations%20programs%20hosts%20clubs%20events%20competitions%20all%20linked%20in%20a%20beautiful%20circular%20network%20design%20clean%20modern%20minimalist%20aesthetic%20with%20warm%20tones%20and%20clear%20visual%20hierarchy&width=800&height=450&seq=welcome-ecosystem-rfw&orientation=landscape',
    ctaLabel: 'Ver el ecosistema',
    ctaLink: '/',
  },
  {
    id: 'step-1-3',
    topic: 'que-es-rfw',
    format: 'video',
    order: 3,
    title: 'RFW en 30 segundos',
    description: 'Un recorrido rápido por todo lo que podés hacer en RFW. Del dial a la comunidad, en medio minuto.',
    icon: 'ri-play-circle-line',
    durationSeconds: 30,
    visualUrl: 'https://readdy.ai/api/search-image?query=Video%20player%20interface%20with%20play%20button%20overlaid%20on%20vibrant%20community%20radio%20scene%20diverse%20people%20engaging%20with%20content%20thumbnail%20style%20digital%20illustration%20warm%20energetic%20colors%20modern%20web%20design%20aesthetic%20clean%20and%20inviting&width=800&height=450&seq=welcome-video-overview&orientation=landscape',
    ctaLabel: 'Ver video',
    ctaLink: '#',
  },

  // ---- TEMA 2: Cómo Participar ----
  {
    id: 'step-2-1',
    topic: 'como-participar',
    format: 'paso',
    order: 1,
    title: 'Entrá a una Cabina',
    description: 'La cabina es el chat en vivo de cada radio. Saludá, pedí canciones, comentá el programa. Los locutores te leen en vivo.',
    icon: 'ri-chat-voice-line',
    durationSeconds: 10,
    visualUrl: 'https://readdy.ai/api/search-image?query=Live%20radio%20chat%20interface%20with%20messages%20flowing%20in%20real%20time%20colorful%20user%20avatars%20engaging%20conversation%20warm%20studio%20atmosphere%20digital%20illustration%20style%20modern%20clean%20UI%20elements%20showing%20active%20community%20interaction%20editorial%20quality&width=800&height=450&seq=welcome-cabina-chat&orientation=landscape',
    ctaLabel: 'Ir a una cabina',
    ctaLink: '/radios-en-vivo',
  },
  {
    id: 'step-2-2',
    topic: 'como-participar',
    format: 'guia',
    order: 2,
    title: 'Reaccioná y Comentá',
    description: 'Cada publicación, foto, video y evento tiene su espacio para reaccionar. 8 reacciones diferentes para expresar exactamente lo que sentís.',
    icon: 'ri-emotion-line',
    durationSeconds: 8,
    visualUrl: 'https://readdy.ai/api/search-image?query=Set%20of%20eight%20colorful%20reaction%20emoji%20icons%20displayed%20in%20a%20row%20with%20labels%20clean%20modern%20interface%20design%20showing%20social%20engagement%20features%20warm%20and%20inviting%20aesthetic%20digital%20illustration%20with%20soft%20shadows%20and%20organized%20layout&width=800&height=450&seq=welcome-reactions-guide&orientation=landscape',
    ctaLabel: 'Ver reacciones',
    ctaLink: '/reacciones',
  },
  {
    id: 'step-2-3',
    topic: 'como-participar',
    format: 'video',
    order: 3,
    title: 'Tu primera interacción',
    description: 'Te mostramos cómo entrar a tu primera cabina, enviar un mensaje y recibir respuesta en vivo. Es más fácil de lo que parece.',
    icon: 'ri-play-circle-line',
    durationSeconds: 20,
    visualUrl: 'https://readdy.ai/api/search-image?query=Step%20by%20step%20tutorial%20style%20illustration%20showing%20person%20opening%20radio%20app%20joining%20live%20chat%20typing%20message%20and%20seeing%20host%20respond%20warm%20encouraging%20colors%20digital%20illustration%20modern%20clean%20design%20with%20numbered%20steps%20visual%20guide&width=800&height=450&seq=welcome-video-first-chat&orientation=landscape',
    ctaLabel: 'Ver cómo se hace',
    ctaLink: '#',
  },

  // ---- TEMA 3: Cómo Ganar Puntos ----
  {
    id: 'step-3-1',
    topic: 'como-ganar-puntos',
    format: 'paso',
    order: 1,
    title: 'Escuchá Radio = Puntos',
    description: 'Cada 15 minutos de escucha suman puntos. Simple. Dejá la radio de fondo y tus puntos suben solos.',
    icon: 'ri-headphone-line',
    durationSeconds: 10,
    visualUrl: 'https://readdy.ai/api/search-image?query=Modern%20illustration%20showing%20person%20with%20headphones%20listening%20to%20radio%20while%20points%20accumulate%20visually%20represented%20as%20glowing%20stars%20or%20tokens%20rising%20upward%20clean%20minimalist%20design%20warm%20inviting%20colors%20digital%20illustration%20style%20with%20clear%20visual%20metaphor&width=800&height=450&seq=welcome-listen-points&orientation=landscape',
    ctaLabel: 'Escuchar radio ahora',
    ctaLink: '/radios-en-vivo',
  },
  {
    id: 'step-3-2',
    topic: 'como-ganar-puntos',
    format: 'guia',
    order: 2,
    title: 'Las 5 actividades que más suman',
    description: 'Escuchar (10 pts), Comentar (5 pts), Compartir (8 pts), Participar en concursos (15 pts), Entrar a Cabina (12 pts). Bonus por racha diaria.',
    icon: 'ri-bar-chart-line',
    durationSeconds: 12,
    visualUrl: 'https://readdy.ai/api/search-image?query=Clean%20infographic%20showing%20five%20activity%20types%20with%20point%20values%20and%20icons%20listen%20comment%20share%20participate%20join%20organized%20in%20a%20modern%20card%20layout%20warm%20amber%20and%20rose%20accents%20digital%20illustration%20with%20clear%20visual%20hierarchy%20simple%20elegant%20design&width=800&height=450&seq=welcome-points-guide&orientation=landscape',
    ctaLabel: 'Ver gamificación',
    ctaLink: '/gamificacion',
  },
  {
    id: 'step-3-3',
    topic: 'como-ganar-puntos',
    format: 'video',
    order: 3,
    title: 'Racha diaria: el multiplicador',
    description: 'Mantené tu racha diaria y tus puntos se multiplican. Día 1: x1, Día 7: x1.5, Día 30: x3, Día 100: x5. ¡La constancia paga!',
    icon: 'ri-play-circle-line',
    durationSeconds: 15,
    visualUrl: 'https://readdy.ai/api/search-image?query=Visual%20representation%20of%20streak%20multiplier%20growing%20from%20x1%20to%20x5%20over%20time%20with%20flame%20icons%20getting%20larger%20and%20brighter%20each%20day%20digital%20illustration%20style%20warm%20gradient%20from%20amber%20to%20fuchsia%20clean%20modern%20motivational%20design&width=800&height=450&seq=welcome-video-streak&orientation=landscape',
    ctaLabel: 'Ver rachas',
    ctaLink: '/rachas',
  },

  // ---- TEMA 4: Cómo Subir Ranking ----
  {
    id: 'step-4-1',
    topic: 'como-subir-ranking',
    format: 'paso',
    order: 1,
    title: 'El Score RFW',
    description: 'Tu puntuación general se calcula con 7 factores: escucha, interacción social, concursos, clubes, eventos, rachas y contribuciones. Cada acción pesa distinto.',
    icon: 'ri-line-chart-line',
    durationSeconds: 12,
    visualUrl: 'https://readdy.ai/api/search-image?query=Clean%20modern%20dashboard%20showing%20score%20composition%20with%20seven%20weighted%20factors%20represented%20as%20colorful%20segments%20in%20a%20radial%20chart%20digital%20illustration%20with%20professional%20analytics%20aesthetic%20violet%20and%20teal%20tones%20crisp%20typography&width=800&height=450&seq=welcome-score-rfw&orientation=landscape',
    ctaLabel: 'Ver rankings',
    ctaLink: '/rankings',
  },
  {
    id: 'step-4-2',
    topic: 'como-subir-ranking',
    format: 'guia',
    order: 2,
    title: 'Subí de Nivel',
    description: 'De Nivel 1 a Nivel 100. Cada nivel trae beneficios: más visibilidad, insignias exclusivas, acceso VIP y prioridad en concursos.',
    icon: 'ri-medal-line',
    durationSeconds: 10,
    visualUrl: 'https://readdy.ai/api/search-image?query=Progressive%20level%20system%20visualization%20from%201%20to%20100%20with%20milestone%20markers%20at%20levels%2010%2025%2050%2075%20100%20each%20with%20badge%20icon%20rewards%20growing%20more%20impressive%20digital%20illustration%20clean%20modern%20design%20violet%20gradient%20elegant%20aesthetic&width=800&height=450&seq=welcome-levels-guide&orientation=landscape',
    ctaLabel: 'Ver niveles',
    ctaLink: '/niveles',
  },
  {
    id: 'step-4-3',
    topic: 'como-subir-ranking',
    format: 'video',
    order: 3,
    title: 'Del Nivel 1 al Top 100',
    description: 'Estrategias reales de oyentes que llegaron al top 100 en menos de 3 meses. Spoiler: constancia y comunidad.',
    icon: 'ri-play-circle-line',
    durationSeconds: 20,
    visualUrl: 'https://readdy.ai/api/search-image?query=Tutorial%20style%20illustration%20showing%20upward%20progression%20path%20with%20milestones%20tips%20and%20strategies%20digital%20illustration%20warm%20motivational%20colors%20clean%20clear%20visual%20storytelling%20modern%20design%20aesthetic&width=800&height=450&seq=welcome-video-ranking-tips&orientation=landscape',
    ctaLabel: 'Ver estrategias',
    ctaLink: '#',
  },

  // ---- TEMA 5: Cómo Entrar a Clubes ----
  {
    id: 'step-5-1',
    topic: 'como-entrar-clubes',
    format: 'paso',
    order: 1,
    title: 'Encontrá tu Club',
    description: 'Cada radio tiene su club de fans. Buscá tu emisora favorita y unite con un clic. Hay 27 clubes activos con más de 85,000 miembros.',
    icon: 'ri-search-line',
    durationSeconds: 10,
    visualUrl: 'https://readdy.ai/api/search-image?query=Visual%20map%20of%20fan%20clubs%20connected%20to%20radio%20stations%20with%20member%20counts%20activity%20indicators%20vibrant%20community%20atmosphere%20digital%20illustration%20with%20sky%20blue%20and%20teal%20accents%20clean%20modern%20network%20visualization%20warm%20inclusive%20design&width=800&height=450&seq=welcome-find-club&orientation=landscape',
    ctaLabel: 'Explorar clubes',
    ctaLink: '/clubes',
  },
  {
    id: 'step-5-2',
    topic: 'como-entrar-clubes',
    format: 'guia',
    order: 2,
    title: 'Qué hacer en tu Club',
    description: 'Participá en retos, ganá insignias de club, conocé a otros fans, asistí a eventos exclusivos y competí por el Top Member.',
    icon: 'ri-flag-line',
    durationSeconds: 10,
    visualUrl: 'https://readdy.ai/api/search-image?query=Infographic%20showing%20club%20activities%20challenges%20badge%20rewards%20exclusive%20events%20and%20member%20ranking%20system%20digital%20illustration%20clean%20modern%20design%20with%20emerald%20and%20sky%20blue%20palette%20organized%20card%20layout%20welcoming%20aesthetic&width=800&height=450&seq=welcome-club-activities&orientation=landscape',
    ctaLabel: 'Ver retos de clubes',
    ctaLink: '/retos-clubes',
  },
  {
    id: 'step-5-3',
    topic: 'como-entrar-clubes',
    format: 'video',
    order: 3,
    title: 'Unite a tu primer club',
    description: 'Te guiamos paso a paso: buscá tu radio, entrá al club, presentate y empezá tu primer reto. En menos de 1 minuto.',
    icon: 'ri-play-circle-line',
    durationSeconds: 15,
    visualUrl: 'https://readdy.ai/api/search-image?query=Step%20by%20step%20tutorial%20showing%20joining%20process%20enter%20radio%20page%20click%20club%20button%20introduce%20yourself%20start%20first%20challenge%20warm%20encouraging%20colors%20clean%20digital%20illustration%20with%20numbered%20visual%20steps%20modern%20friendly%20design&width=800&height=450&seq=welcome-video-join-club&orientation=landscape',
    ctaLabel: 'Unirme ahora',
    ctaLink: '/clubes',
  },
];

export const welcomeVideos: WelcomeVideo[] = [
  {
    id: 'vid-1',
    topic: 'que-es-rfw',
    title: 'RFW en 30 segundos',
    durationSeconds: 30,
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Video%20thumbnail%20with%20play%20button%20over%20vibrant%20community%20radio%20scene%20diverse%20happy%20people%20connecting%20through%20radio%20waves%20warm%20inviting%20colors%20modern%20clean%20design%20editorial%20quality%20thumbnail%20style&width=800&height=450&seq=welcome-thumb-overview&orientation=landscape',
    description: 'Un recorrido relámpago por toda la plataforma.',
  },
  {
    id: 'vid-2',
    topic: 'como-participar',
    title: 'Tu primera interacción',
    durationSeconds: 20,
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Video%20thumbnail%20showing%20person%20typing%20in%20live%20chat%20with%20radio%20host%20responding%20in%20real%20time%20warm%20studio%20lighting%20inviting%20atmosphere%20clean%20modern%20design%20tutorial%20style%20thumbnail&width=800&height=450&seq=welcome-thumb-interaction&orientation=landscape',
    description: 'Cómo entrar a una cabina y participar en vivo.',
  },
  {
    id: 'vid-3',
    topic: 'como-ganar-puntos',
    title: 'Racha diaria explicada',
    durationSeconds: 15,
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Video%20thumbnail%20with%20flame%20icon%20and%20multiplier%20numbers%20x1%20to%20x5%20growing%20progressively%20brighter%20warm%20gradient%20amber%20to%20fuchsia%20clean%20modern%20design%20motivational%20tutorial%20style&width=800&height=450&seq=welcome-thumb-streak&orientation=landscape',
    description: 'Cómo funciona el multiplicador de racha diaria.',
  },
  {
    id: 'vid-4',
    topic: 'como-subir-ranking',
    title: 'Del Nivel 1 al Top 100',
    durationSeconds: 20,
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Video%20thumbnail%20showing%20upward%20progression%20arrow%20with%20milestone%20markers%20and%20trophy%20at%20top%20clean%20modern%20design%20violet%20gradient%20motivational%20tutorial%20style%20digital%20illustration&width=800&height=450&seq=welcome-thumb-ranking&orientation=landscape',
    description: 'Estrategias para escalar en el ranking.',
  },
  {
    id: 'vid-5',
    topic: 'como-entrar-clubes',
    title: 'Unite a tu primer club',
    durationSeconds: 15,
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Video%20thumbnail%20showing%20person%20clicking%20join%20button%20on%20club%20page%20with%20welcoming%20community%20members%20waving%20warm%20sky%20blue%20tones%20clean%20modern%20tutorial%20style%20digital%20illustration%20inviting%20friendly%20design&width=800&height=450&seq=welcome-thumb-join-club&orientation=landscape',
    description: 'Guía paso a paso para unirte a un club de fans.',
  },
];

export const welcomeGuides: WelcomeGuide[] = [
  {
    id: 'guide-1',
    topic: 'que-es-rfw',
    title: 'Mapa del ecosistema RFW',
    icon: 'ri-global-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    content: 'RFW conecta 6 pilares: Radios, Programas, Locutores, Oyentes, Clubes y Eventos. Cada pilar tiene sus propias páginas, rankings y sistemas de interacción. Todo está interconectado: lo que hacés en un club suma puntos que mejoran tu ranking global.',
    visualUrl: 'https://readdy.ai/api/search-image?query=Clean%20modern%20infographic%20showing%20six%20interconnected%20pillars%20of%20a%20radio%20social%20network%20radios%20programs%20hosts%20listeners%20clubs%20events%20arranged%20in%20a%20circular%20hub%20and%20spoke%20diagram%20digital%20illustration%20emerald%20and%20teal%20tones%20elegant%20professional%20design&width=800&height=500&seq=welcome-guide-ecosystem&orientation=landscape',
  },
  {
    id: 'guide-2',
    topic: 'como-participar',
    title: 'Guía de interacción rápida',
    icon: 'ri-chat-smile-2-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    content: '1. Elegí una radio en vivo. 2. Entrá a la cabina (botón verde). 3. Escribí tu mensaje. 4. Usá reacciones para expresarte sin escribir. 5. Seguí a locutores y oyentes que te gusten.',
    visualUrl: 'https://readdy.ai/api/search-image?query=Numbered%20step%20by%20step%20visual%20guide%20showing%20radio%20selection%20live%20chat%20entry%20message%20typing%20reaction%20buttons%20and%20follow%20actions%20clean%20modern%20amber%20toned%20infographic%20digital%20illustration%20clear%20instructional%20design&width=800&height=500&seq=welcome-guide-interaction&orientation=landscape',
  },
  {
    id: 'guide-3',
    topic: 'como-ganar-puntos',
    title: 'Tabla de puntos por actividad',
    icon: 'ri-coin-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    content: 'Escuchar radio (15 min): 10 pts. Comentar: 5 pts. Compartir: 8 pts. Participar en concurso: 15 pts. Entrar a cabina: 12 pts. Completar trivia: 20 pts. Ganar competencia: 50 pts. Racha diaria: multiplicador x1 a x5.',
    visualUrl: 'https://readdy.ai/api/search-image?query=Clean%20data%20table%20infographic%20showing%20activity%20types%20with%20associated%20point%20values%20and%20streak%20multipliers%20organized%20in%20descending%20order%20rose%20and%20amber%20accent%20colors%20modern%20professional%20design%20digital%20illustration%20with%20clear%20visual%20hierarchy&width=800&height=500&seq=welcome-guide-points-table&orientation=landscape',
  },
  {
    id: 'guide-4',
    topic: 'como-subir-ranking',
    title: 'Los 7 factores del Score',
    icon: 'ri-trophy-line',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    content: 'Tu Score RFW se compone de: Escucha (25%), Interacción Social (20%), Concursos (15%), Clubes (15%), Eventos (10%), Rachas (10%), Contribuciones (5%). Cada factor tiene sub-actividades que suman puntos diarios.',
    visualUrl: 'https://readdy.ai/api/search-image?query=Pie%20chart%20or%20donut%20chart%20infographic%20showing%20seven%20weighted%20score%20factors%20with%20percentages%20and%20icons%20violet%20teal%20and%20rose%20palette%20clean%20modern%20design%20professional%20data%20visualization%20digital%20illustration%20elegant%20analytical%20aesthetic&width=800&height=500&seq=welcome-guide-score-factors&orientation=landscape',
  },
  {
    id: 'guide-5',
    topic: 'como-entrar-clubes',
    title: 'Camino rápido al club',
    icon: 'ri-team-line',
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    content: '1. Buscá tu radio favorita. 2. Click en "Club". 3. "Unirme" (es gratis). 4. Presentate en el muro. 5. Elegí tu primer reto. 6. ¡Empezá a sumar insignias de club!',
    visualUrl: 'https://readdy.ai/api/search-image?query=Six%20step%20numbered%20visual%20guide%20showing%20club%20joining%20process%20from%20radio%20search%20to%20completing%20first%20challenge%20clean%20modern%20sky%20blue%20and%20teal%20infographic%20digital%20illustration%20clear%20instructional%20steps%20welcoming%20design&width=800&height=500&seq=welcome-guide-join-club&orientation=landscape',
  },
];

export const welcomeStats: WelcomeStats = {
  totalNewUsersToday: 1284,
  totalCompletedOnboarding: 1103,
  averageCompletionSeconds: 87,
  completionRate: 85.9,
  mostViewedTopic: 'como-ganar-puntos',
  mostSkippedTopic: 'como-entrar-clubes',
};

export const welcomeRules: WelcomeRule[] = [
  {
    id: 1,
    title: 'Menos de 2 minutos',
    description: 'El onboarding completo dura 87 segundos en promedio. Cada paso está diseñado para ser digerido en segundos, no en minutos. Si algo toma más de 20 segundos, lo partimos en pasos más chicos. La meta: que en menos de 2 minutos sepas exactamente qué es RFW y cómo empezar a disfrutarlo.',
    icon: 'ri-timer-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
  },
  {
    id: 2,
    title: 'Tres formatos, un objetivo',
    description: 'Pasos rápidos para los que quieren ya. Videos cortos (15-30 segundos) para los que prefieren ver. Guías visuales para los que quieren profundizar. Cada usuario elige su camino, pero todos llegan al mismo destino: entender RFW y querer quedarse.',
    icon: 'ri-stack-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
  {
    id: 3,
    title: 'Celebrar cada paso completado',
    description: 'Cada tema completado dispara una micro-celebración: una animación sutil, un mensaje de aliento. No es un tutorial aburrido — es una bienvenida cálida. Porque la primera impresión define si alguien se queda o se va.',
    icon: 'ri-sparkling-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
  },
];

export function getStepsByTopic(topic: WelcomeTopic | 'todas'): WelcomeStep[] {
  if (topic === 'todas') return welcomeSteps;
  return welcomeSteps.filter((s) => s.topic === topic).sort((a, b) => a.order - b.order);
}

export function getStepsByFormat(format: WelcomeFormat | 'todas', topic?: WelcomeTopic | 'todas'): WelcomeStep[] {
  let steps = topic && topic !== 'todas' ? getStepsByTopic(topic) : welcomeSteps;
  if (format !== 'todas') {
    steps = steps.filter((s) => s.format === format);
  }
  return steps;
}

export function getVideosByTopic(topic: WelcomeTopic | 'todas'): WelcomeVideo[] {
  if (topic === 'todas') return welcomeVideos;
  return welcomeVideos.filter((v) => v.topic === topic);
}

export function getGuidesByTopic(topic: WelcomeTopic | 'todas'): WelcomeGuide[] {
  if (topic === 'todas') return welcomeGuides;
  return welcomeGuides.filter((g) => g.topic === topic);
}

export function getTotalOnboardingSeconds(): number {
  return welcomeSteps.reduce((sum, s) => sum + s.durationSeconds, 0);
}

export function getTopicProgress(topic: WelcomeTopic): number {
  const topicSteps = getStepsByTopic(topic);
  return topicSteps.length;
}